import React, { useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPlay } from "@fortawesome/free-solid-svg-icons";
import { FaAngleRight } from 'react-icons/fa';
import ClientsSlider from '../Header/ClientsSlider';

const faqList = [
  {
    question: "What is the difference between term life and whole life insurance?",
    answer:
      "Term life insurance provides coverage for a specific period of time, while whole life insurance covers you for your entire life and includes a savings component called cash value.",
  },
  {
    question: "How can I start investing with a small budget?",
    answer:
      "You can start investing with a small amount by exploring options like mutual funds, index funds, or micro-investing apps that allow fractional share purchases.",
  },
  {
    question: "What does an insurance deductible mean?",
    answer:
      "An insurance deductible is the amount you agree to pay out of pocket before your insurance company covers the remaining cost of a claim.",
  },
  {
    question: "Why is building an emergency fund important?",
    answer:
      "An emergency fund provides financial security in case of unexpected expenses like medical emergencies, job loss, or car repairs, reducing the need to rely on loans or credit cards.",
  },
  {
    question: "How do credit scores affect loan approvals?",
    answer:
      "Your credit score plays a significant role in determining your eligibility for loans and interest rates. A higher credit score increases your chances of approval and getting better terms.",
  },
];


// FAQ Item Component
const FaqItem = ({ faq, index, isOpen, onToggle }) => {
  return (
    <div className={`${isOpen ? "active" : ""} rounded-lg`}>
      <a
        href="#!"
        className="btn p-4 lg:p-6 w-full text-start flex justify-between items-center cursor-pointer"
        onClick={() => onToggle(index)} // Toggle FAQ on click
      >
        <span>{faq.question}</span>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
      </a>
      <div
        className={`${isOpen ? "block" : "hidden"} p-4 lg:p-6 bg-white shadow  rounded-xl`}
      >
        <p className="opacity-50">{faq.answer}</p>
      </div>
    </div>
  );
};

FaqItem.propTypes = {
  faq: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const mistakes = [
	{
		title: "Delay in purchasing the policy",
		text: "Buying a life insurance policy at an early age when you are young and healthy is the best way to get maximum coverage at a lower premium.",
	},
	{
		title: "Not purchasing adequate coverage and tenure",
		text: "Ensure that you are adequately insured for a maximum tenure possible, recognizing your Human Life Value.",
	},
	{
		title: "Providing incorrect information",
		text: "Concealing important personal and health information, intentionally or unintentionally, can lead to the insurance company rejecting your claim.",
	},
	{
		title: "Not doing proper research",
		text: "Doing thorough research by comparing the policy features and benefits, premiums, terms and conditions, claim processing time, fees, etc., can help you make the best and suitable choice.",
	},
	{
		title: "Not reading the fine print",
		text: "Make sure you read all the policy documents containing vital information to make an informed decision and avoid any disputes in the future. Clarify all your doubts.",
	},
	{
		title: "Not informing the family",
		text: "Your family will not financially benefit from your life insurance policy if they are uninformed.",
	},
];

const MistakeItem = ({ item, index }) => (
	<div className="xl:p-4">
		<div className="w-12 h-12 rounded-full text-xl inline-flex justify-center items-center bg-darkBlue text-white mb-4">
			{index}
		</div>
		<h4 className="font-bold text-xl mb-4">{item.title}</h4>
		<p className="opacity-80 mb-0">{item.text}</p>
	</div>
);

MistakeItem.propTypes = {
	item: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
};

// FAQs Component
const FAQs = () => {
  // Set the first FAQ (index 0) to be open by default
  const [openIndex, setOpenIndex] = useState(0); 

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index); // Close the FAQ if it's al
    // ready open, else open it
  };

  return (
    <React.Fragment>
      {/* New Section Above FAQ */}
      <section className="bg-darkBlue text-white min-h-[30vh] md:min-h-[30vh] flex items-center justify-center" style={{ paddingTop: '0.5rem' }}>
        <div className="max-w-7xl px-4 mx-auto">
          <div className="grid grid-cols-12 items-center justify-center">
            <div className="col-span-12 text-center">
              <div className="py-8">
                <h2 className="text-2xl md:text-[40px] text-center leading-none font-bold pt-12 md:pt-10">
                  FAQ's
                </h2>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="ezy__faq8 light py-14 md:py-24 bg-white">
        <div className="max-w-7xl px-4 mx-auto">
          <div className="grid grid-cols-12 justify-between gap-6">
            <div className="col-span-12 md:col-span-5">
              <h2 className="text-3xl font-bold leading-tight md:text-[40px] mb-4 uppercase">
                Frequently Asked Questions
              </h2>
             
              {/* <button className="bg-transparent hover:bg-blue-600 border border-blue-600 hover:text-white rounded transition mt-6 lg:mt-12 px-7 py-3 text-blue-600">
                View All FAQ's
              </button> */}
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7">
              {faqList.map((faq, i) => (
                <FaqItem
                  faq={faq}
                  index={i} // Pass the index for each FAQ
                  key={i}
                  isOpen={openIndex === i} // Check if this FAQ is open
                  onToggle={handleToggle} // Pass the toggle handler
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <ClientsSlider className= "bg-skyBlue"/>

      <section className="ezy__howitworks2 light py-14 md:py-24 bg-white">
			<div className="max-w-7xl px-4 mx-auto">
				<div className="grid grid-cols-12 gap-6">
					<div className="col-span-12 md:col-span-7">
						<h1 className="text-3xl font-bold md:text-[40px] leading-tight mb-6">
            Six Mistakes to Avoid When Buying a Life Insurance Policy
						</h1>
						<p className="text-[17px] leading-normal opacity-80">
            Avoid these common mistakes to make the best decisions for your life insurance policy and secure your family's future.
						</p>
					</div>
					<div className="col-span-12 md:col-span-5 md:col-start-8 relative">
						<img
							src="https://images.pexels.com/photos/7734672/pexels-photo-7734672.jpeg?auto=compress&cs=tinysrgb&w=600"
							alt=""
							className="max-w-full h-[40vh] mx-auto" //rounded-[10px]
						/>
					</div>
				</div>
				<div className="grid grid-cols-6 gap-6 justify-center items-center mt-12">
					{mistakes.map((item, i) => (
						<div className="col-span-6 md:col-span-3 lg:col-span-2" key={i}>
							<MistakeItem index={i + 1} item={item} />
						</div>
					))}
				</div>
			</div>
		</section>
 
      
    </React.Fragment>
  );
};

export default FAQs;
